import { Helmet } from "react-helmet";
import { useState, useEffect } from "react"
import { Img, Heading, Button } from "../../components";
import { ReactTable } from "../../components/ReactTable";
import Navbar from "components/Navbar";
import { createColumnHelper } from "@tanstack/react-table";
import React from "react";
import { useNavigate } from "react-router-dom";
import CheckBox from "components/CheckBox";
import axios from "axios"
import { useAppContext } from "components/Context/AppContext";
import SecurityUploadSection from "./UploadSection";

function createColumn(accessor, label) {
  const tableColumnHelper = createColumnHelper()
  return tableColumnHelper.accessor(accessor, {
    cell: (info) => (
      <Heading as="p" size="textxs" className="text-white-a700">
        {info.getValue()}
      </Heading>
    ),
    header: () => (
      <Heading
        size="headingxs"
        as="h6"
        className="pb-[22px] text-left text-[12px] font-bold text-white-a700 sm:pb-5"
      >
        {label}
      </Heading>
    ),
    meta: { width: "104px" },
  });
}

const apiUrl = process.env.REACT_APP_API_URL;

export default function PageUploadSecurity() {

  // table data
  const [data, setData] = useState([]);
  const [token, setToken] = useState(null);
  const [refreshDB, setRefreshDB] = useState(false);
  const { isProcessRunning } = useAppContext();
  
  const navigate = useNavigate();

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    setToken(token)
    const fetchData = async () => {
      try {
        const response = await axios.post(`${apiUrl}/get_upload_security`, {},
          {
            params: {
              token
            },
            headers: {
              'accept': 'application/json',
              'Content-Type': 'application/json',
            },
          }
        );
        console.log('API Response:', response.data.TableData);
        const isChecked = false
        const fetchedData = response.data.TableData.map((row, index) =>
          ({ ...row, isChecked })
        );
        
        setData(fetchedData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };
    fetchData();

    let intervalId;
    if (isProcessRunning) {
      intervalId = setInterval(fetchData, 3000);  // Poll every 2 seconds
    }

    return () => clearInterval(intervalId);  // Clean up interval on component unmount

  }, [refreshDB, isProcessRunning]);

  
  const tableColumns = React.useMemo(() => {
    const tableColumnHelper = createColumnHelper();
    return [

      createColumn("upload_name", "Name"),
      createColumn("upload_id", "Process ID"),
      createColumn("created_by", "Created By"),
      createColumn("create_date", "Created Date"),

      tableColumnHelper.accessor("status", {
        cell: (info) => {
          const status = info.getValue();
          const url = info.row.original.report_url; 
          const filename = info.row.original.filename;
          const result_filename = info.row.original.result_filename;
          const err_filename = info.row.original.err_filename;

          return status === 'success' ? (
            <a href={apiUrl + '/sup_download/model/' + result_filename + "?token=" + token} className="text-green-500 underline" target="_blank" rel="noopener noreferrer">
              success
            </a>
          ) : (
            status === 'error' ? (
              <a href={apiUrl + '/sup_download/model/' + err_filename + "?token=" + token} className="text-red-500 underline">
                error
              </a>
            ): (
              <>
                <Heading as="p" size="textxs" className="text-white-a700">
                  {status}
                </Heading>
            </>
            )
          );
        },
        header: () => (
          <Heading
            size="headingxs"
            as="h6"
            className="pb-[22px] text-left text-[12px] font-bold text-white-a700 sm:pb-5"
          >
            Status
          </Heading>
        ),
        meta: { width: "104px" },
      }),
      
      tableColumnHelper.accessor("filename", {
        cell: (info) => {
          const filename = info.getValue();
          const url = apiUrl +'/sup_download/model/' + filename + "?token=" + token

          return <a href={url} className="text-white-a700 underline" >
            download
          </a>
        },
        header: () => (
          <Heading
            size="headingxs"
            as="h6"
            className="pb-[22px] text-left text-[12px] font-bold text-white-a700 sm:pb-5"
          >
            Download
          </Heading>
        ),
        meta: { width: "104px" },
      }),
    ];
    
  });

  // checked rows
  const checkedNames = data? data.filter(row => row.isChecked).map(row => row.port_id) : [];

  return (
    <>
       
      <Helmet>
        <title>Upload New Security</title>
        <meta
          name="description"
          content="Upload new securities."
        />
      </Helmet>

      <div className="w-full flex flex-col" >
      <div className="relative h-[2000px] bg-gray-900_01">
            {/* Navigation Bar */}
            <div className="container-xs absolute left-0 right-0 top-6 my-auto">
                <Navbar />
            </div>  
    
            <div className="container-xs absolute bottom-0 left-0 right-0 top-24 flex h-max flex-col gap-[52px] md:px-5 sm:gap-[26px]">
            
                <div className="flex flex-col items-start gap-1.5">
                <div className="flex flex-col items-center gap-[18px] self-stretch">
                
                    <Heading
                    size="text2xl"
                    as="h1"
                    className="text-[44px] font-medium text-white-a700 md:text-[40px] sm:text-[34px]"
                    >
                    Upload New Security
                    </Heading>

                    <SecurityUploadSection />

                    <div className="mb-[30px] flex flex-col gap-7 self-stretch">
                      <ReactTable
                          size="xs"
                          bodyProps={{ }}
                          headerProps={{ className: "border-white-a700 border-b border-solid" }}
                          cellProps={{ style: { fontSize: "10px", color: "#ffffff" }, className: "border-white-a700 border-b border-solid text-xs" }}
                          className="md:block md:overflow-x-auto md:whitespace-nowrap"
                          columns={tableColumns}
                          data={data}
                      />

                  </div>
                </div>
                <div className="fixed bottom-0 left-0 flex items-center gap-[22px] bg-gray-900_02 p-1">
                    <Heading
                    size="texts"
                    as="p"
                    className="self-end text-[14px] font-medium tracking-[-0.28px] text-gray-400"
                    >
                    Powered by
                    </Heading>
                    <Img src="images/img_settings.svg" alt="Settings Image" className="h-[16px] object-contain" />
                </div>
                </div>
            </div>
            </div>
        </div>
    </>
  );
}
